import { Box, Button, TextField } from '@mui/material';
import { DrawerHeader } from '../../../../components/Drawer/DrawerHeader';
import VisitDetails from '../VisitaDetails';
import { DRAWER_ACTION_VALUES } from './DrawerManager';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks';
import { hideAlert, showAlert } from '../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../constants/values';
import { enqueueSnackbar } from 'notistack';
import { RootState } from '../../../../redux/store';
import {
    useCancelVisitMutation,
    useRejectVisitMutation
} from '../../../../services/visitasApi';
import { useGetEstablishmentDetailQuery } from '../../../../services/companyApi';
import { useLocation } from 'react-router-dom';
import { getDateTime } from '../../../../utils/common';
import { TFunction } from 'i18next';
import EstablishmentDetails from '../EstablishmentDetails';

const CancelVisitDrawer = ({
    translate,
    selected,
    setOpen,
    onSuccess,
    actionType
}: {
    selected: any;
    setOpen: (value: boolean) => void;
    onSuccess?: () => void;
    translate: TFunction;
    actionType: string;
}) => {
    const location = useLocation();
    const [observacion, setObservacion] = useState('');
    const dispatch = useAppDispatch();

    const user = useAppSelector((state: RootState) => state.auth.user);
    const idEstablecimiento = location.state?.selectedVisita?.idEstablecimiento;

    const [cancelVisit, { isLoading: isCancelLoading }] = useCancelVisitMutation();

    const [rejectVisit, { isLoading: isRejectLoading }] = useRejectVisitMutation();

    const isLoading = isCancelLoading || isRejectLoading;

    const { data: establishmentDetails, isLoading: loadingEstablishment } =
        useGetEstablishmentDetailQuery(
            { establishmentId: idEstablecimiento },
            { skip: !idEstablecimiento }
        );

    const handleConfirm = async () => {
        if (!selected) return;

        if (!observacion.trim()) {
            enqueueSnackbar(
                translate('drawer.warningReason', { action: actionType?.toLowerCase() }),
                { variant: 'warning' }
            );
            return;
        }

        try {
            if (actionType === DRAWER_ACTION_VALUES.REJECT_VISIT) {
                await rejectVisit({
                    visitId: selected.id,
                    motivo: observacion.trim()
                }).unwrap();

                enqueueSnackbar(translate('drawer.successReject'), {
                    variant: 'success'
                });

                onSuccess?.();
                setOpen(false);
                return;
            }

            if (actionType === DRAWER_ACTION_VALUES.CANCEL_VISIT) {
                const fechaHora = getDateTime(new Date().toISOString());
                const observacionPrevia = selected.observacion ? `${selected.observacion}\n` : '';
                const nombreUsuario = user?.nombre || '';
                const cuilUsuario = user?.cuil || '';
                const observacionFinal = `${observacionPrevia}${fechaHora} - Cancelado por ${nombreUsuario} (CUIL: ${cuilUsuario}) - Motivo: ${observacion.trim()}`;

                await cancelVisit({
                    visitId: selected.id,
                    observacion: observacionFinal
                }).unwrap();

                enqueueSnackbar(translate('drawer.successCancel'), {
                    variant: 'success'
                });

                onSuccess?.();
                setOpen(false);
            }
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo actualizar la visita',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const confirmAction = () => {
        dispatch(
            showAlert({
                title: translate(
                    actionType === DRAWER_ACTION_VALUES.CANCEL_VISIT
                        ? 'drawer.confirmCancel'
                        : 'drawer.confirmReject'
                ),
                icon: 'info',
                keepMounted: true,
                confirmText: translate('drawer.confirm'),
                cancelText: translate('drawer.cancel'),
                cancelAction: () => dispatch(hideAlert()),
                confirmAction: handleConfirm
            })
        );
    };
    return (
        <>
            <DrawerHeader
                onIconClick={() => setOpen(false)}
                text={translate(`drawer.title.${actionType?.toLowerCase()}`)}
            />
            <VisitDetails visita={selected} />
            {actionType !== DRAWER_ACTION_VALUES.SEE_DETAIL &&
                actionType !== DRAWER_ACTION_VALUES.ESTABLISHMENT && (
                    <>
                        <TextField
                            label={
                                actionType === DRAWER_ACTION_VALUES.CANCEL_VISIT
                                    ? translate('drawer.cancelReason')
                                    : translate('drawer.rejectReason')
                            }
                            multiline
                            rows={4}
                            fullWidth
                            variant="outlined"
                            value={observacion}
                            inputProps={{ maxLength: 500 }}
                            onChange={(e) => setObservacion(e.target.value)}
                            helperText={`${observacion.length}/500`}
                            error={observacion.length > 0 && observacion.length < 10}
                        />

                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                right: 16,
                                display: 'flex',
                                gap: 2
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setOpen(false)}
                            >
                                {translate('drawer.close')}
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={
                                    actionType === DRAWER_ACTION_VALUES.CANCEL_VISIT
                                        ? handleConfirm
                                        : confirmAction
                                }
                                disabled={isLoading || observacion.trim().length < 10}
                            >
                                {translate(
                                    actionType === DRAWER_ACTION_VALUES.CANCEL_VISIT
                                        ? 'drawer.cancelVisit'
                                        : 'drawer.rejectVisit'
                                )}
                            </Button>
                        </Box>
                    </>
                )}
            <>
                {actionType === DRAWER_ACTION_VALUES.ESTABLISHMENT &&
                    !loadingEstablishment &&
                    establishmentDetails && (
                        <EstablishmentDetails
                            establishment={establishmentDetails}
                            loading={loadingEstablishment}
                        />
                    )}
            </>
        </>
    );
};

export { CancelVisitDrawer };
